/**
 * @fileoverview Página de Dados Históricos & Base de Análise
 * 
 * @module HistoricoFinanceiro
 * @description
 * Exibe DREs históricas completas (2020-2024), gráficos de evolução,
 * análise de tendências e métricas calculadas (CAGR, médias, volatilidade).
 * 
 * @critical FUNDAMENTAL para auditabilidade - mostra origem de todas as projeções
 * 
 * @features
 * - Tabelas DRE completas por empresa
 * - Gráficos de evolução temporal
 * - Cálculo automático de CAGR
 * - Análise de tendências e benchmarks
 * - Tooltips auditáveis em cada número
 * - Exportação de dados
 */

import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Button,
  Breadcrumbs,
  Link as MuiLink,
  Tooltip,
  IconButton,
  Divider,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Download,
  Home,
  TrendingUp as TrendingUpIcon,
  ShowChart as ShowChartIcon,
  Article as ArticleIcon,
  Warning as WarningIcon,
  Lightbulb as LightbulbIcon,
  Assessment as AssessmentIcon,
  Rocket as RocketIcon,
  Edit as EditIcon,
  TrendingUp as TrendingUpIconMui,
  TrendingDown as TrendingDownIconMui,
  Dashboard as DashboardIcon,
  Calculate as CalculateIcon,
  Search as SearchIcon,
  GpsFixed as GpsFixedIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { Lightbulb, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import { DREHistorico, HistoricoEmpresa } from '../parsers/HistoricoParser';
import { useDadosHistoricosIntegrado } from '../hooks/useDadosHistoricosIntegrado';
import { useCellEditStatus } from '../hooks/useCellEditStatus';
import { TooltipAuditavel } from '../components/audit/TooltipAuditavel';
import { CustomChartTooltip } from '../components/charts/CustomChartTooltip';
import { useHorizontalScroll } from '../hooks/useHorizontalScroll';
import { touchFriendlyButton, touchFriendlyIconButton } from '../utils/mobileFriendlyStyles';
import designTokens from '../theme/designTokens';

/**
 * Formata número como moeda BRL
 */
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Formata número como percentual
 */
const formatPercent = (value: number, decimals = 1): string => {
  return `${value.toFixed(decimals)}%`;
};

/**
 * TAREFA 3.2: Benchmarks de Mercado
 * Avalia margem EBITDA em relação a benchmarks típicos do setor de serviços criativos
 */
const getBenchmarkChip = (margemEbitda: number): { text: string; color: 'success' | 'warning' | 'error'; severity: 'success' | 'info' | 'warning' } => {
  // Benchmarks baseados em empresas de serviços criativos e audiovisuais
  // Fontes: Relatórios setoriais de M&A, comparáveis públicos (Netflix, Disney Studios)
  if (margemEbitda >= 15 && margemEbitda <= 25) {
    return {
      text: 'Alinhado com a média do setor (15-25%)',
      color: 'success',
      severity: 'success',
    };
  } else if (margemEbitda > 25) {
    return {
      text: 'Acima da média do setor (>25%) - Performance superior',
      color: 'success',
      severity: 'success',
    };
  } else if (margemEbitda >= 10 && margemEbitda < 15) {
    return {
      text: 'Abaixo da média do setor (10-15%) - Oportunidade de melhoria',
      color: 'warning',
      severity: 'info',
    };
  } else {
    return {
      text: 'Significativamente abaixo do setor (<10%) - Atenção necessária',
      color: 'error',
      severity: 'warning',
    };
  }
};

/**
 * TAREFA 3.3: Glossário de Termos Técnicos
 * Tooltip educacional para termos financeiros
 */
interface GlossaryTooltipProps {
  term: string;
  definition: string;
  formula?: string;
  interpretation?: string;
}

const GlossaryTooltip: React.FC<GlossaryTooltipProps> = ({ term, definition, formula, interpretation }) => {
  const tooltipContent = (
    <Box sx={{ maxWidth: 320 }}>
      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        {term}
      </Typography>
      <Typography variant="body2" gutterBottom>
        {definition}
      </Typography>
      {formula && (
        <>
          <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.3)' }} />
          <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace', bgcolor: 'rgba(0,0,0,0.2)', p: 0.5, borderRadius: 1 }}>
            <strong>Fórmula:</strong> {formula}
          </Typography>
        </>
      )}
      {interpretation && (
        <>
          <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.3)' }} />
          <Typography variant="caption" display="block">
            <strong>Interpretação:</strong> {interpretation}
          </Typography>
        </>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      <IconButton size="small" sx={{ ml: 0.5, p: 0.5 }} aria-label="Informações">
        <InfoIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
};

/**
 * Formata CAGR com cor e ícone
 * Trata casos especiais (NaN, Infinity)
 */
const CAGRChip: React.FC<{ value: number }> = ({ value }) => {
  // Caso 1: NaN - Valores negativos ou zero (cálculo impossível)
  if (isNaN(value)) {
    return (
      <Tooltip title="Cálculo de CAGR prejudicado por valores negativos ou nulos no período" arrow>
        <Chip
          icon={<InfoIcon sx={{ fontSize: 16 }} />}
          label="N/A"
          color="default"
          size="small"
          sx={{ 
            opacity: 0.7,
            cursor: 'help',
          }}
        />
      </Tooltip>
    );
  }
  
  // Caso 2: Infinito (divisão por zero)
  if (!isFinite(value)) {
    return (
      <Tooltip title="Valores extremos impedem cálculo confiável de CAGR" arrow>
        <Chip
          icon={<InfoIcon sx={{ fontSize: 16 }} />}
          label="N/A"
          color="default"
          size="small"
          sx={{ opacity: 0.7 }}
        />
      </Tooltip>
    );
  }
  
  // Caso 3: Valor válido
  const isPositive = value >= 0;
  return (
    <Chip
      icon={isPositive ? <TrendingUpIconMui sx={{ fontSize: 16 }} /> : <TrendingDownIconMui sx={{ fontSize: 16 }} />}
      label={formatPercent(value * 100)}
      color={isPositive ? 'success' : 'error'}
      size="small"
    />
  );
};

/**
 * Card de KPI com tooltip de auditoria e contexto educacional
 */
interface KPICardProps {
  title: string;
  value: string;
  subtitle?: string;
  color?: 'primary' | 'secondary' | 'success' | 'warning';
  tooltip?: string;
  description?: string; // Texto explicativo sempre visível
  interpretation?: string; // Interpretação do valor (ex: "Crescimento sólido", "Volatilidade baixa")
  formula?: string; // Fórmula de cálculo (aparece no tooltip expandido)
  context?: string; // Contexto adicional (aparece no tooltip expandido)
}

const KPICard: React.FC<KPICardProps> = ({ 
  title, 
  value, 
  subtitle, 
  color = 'primary', 
  tooltip,
  description,
  interpretation,
  formula,
  context,
}) => {
  // Construir tooltip rico
  const tooltipContent = (
    <Box sx={{ maxWidth: 400 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>
        {title}
      </Typography>
      
      {tooltip && (
        <Typography variant="body2" paragraph sx={{ color: 'rgba(255,255,255,0.9)' }}>
          {tooltip}
        </Typography>
      )}
      
      {formula && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mt: 1, color: '#FFFFFF' }}>
            📐 Fórmula:
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'rgba(255, 255, 255, 0.1)', p: 0.5, borderRadius: 1, color: '#FFFFFF' }}>
            {formula}
          </Typography>
        </>
      )}
      
      {context && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <Lightbulb size={12} />
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>
              Contexto:
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            {context}
          </Typography>
        </>
      )}
      
      {interpretation && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, color: '#FFFFFF' }}>
            <GpsFixedIcon sx={{ fontSize: 14 }} />
            Interpretação:
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            {interpretation}
          </Typography>
        </>
      )}
    </Box>
  );

  return (
    <Card 
      sx={{ 
        height: '100%',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        }
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography color="text.secondary" variant="body2" fontWeight="bold">
            {title}
          </Typography>
          <Tooltip 
            title={tooltipContent} 
            arrow 
            placement="top"
            // Removido sx inline - usando theme global para light/dark mode
          >
            <IconButton color={color} sx={touchFriendlyIconButton} aria-label={`Informações sobre ${title}`}>
              <InfoIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Typography variant="h4" color={`${color}.main`} gutterBottom>
          {value}
        </Typography>
        
        {subtitle && (
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            {subtitle}
          </Typography>
        )}
        
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
            {description}
          </Typography>
        )}
        
        {interpretation && (
          <Chip 
            label={interpretation}
            size="small"
            color={color}
            // Removido variant="outlined" - usando filled do tema global
            sx={{ mt: 1, fontSize: '0.7rem' }}
          />
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Tabela de DRE Histórica
 */
interface TabelaDREProps {
  empresa: HistoricoEmpresa;
}

const TabelaDRE: React.FC<TabelaDREProps> = ({ empresa }) => {
  const { containerRef, showLeftShadow, showRightShadow } = useHorizontalScroll();
  const getCellEditInfo = useCellEditStatus();
  const anos = Object.keys(empresa.anos).map(Number).sort();

  // Mapear nome da empresa para formato do hook
  const empresaKey = empresa.empresa === 'Sétima' ? 'setima' : 'studio555';

  // 🆕 Helper: Detecta se ano tem dados parciais
  const isDadoParcial = (ano: number): { parcial: boolean; meses?: number; label?: string } => {
    // 2025 = 9 meses (jan-set)
    if (ano === 2025) {
      const dataAtual = new Date();
      const anoAtual = dataAtual.getFullYear();
      if (ano === anoAtual) {
        const mesAtual = dataAtual.getMonth() + 1; // 0-indexed
        return { parcial: true, meses: mesAtual, label: `${mesAtual} meses` };
      }
      return { parcial: true, meses: 9, label: '9 meses' };
    }
    
    // 2023 da 555 Studios = 6 meses (jul-dez)
    if (ano === 2023 && empresa.empresa === '555 Studios') {
      return { parcial: true, meses: 6, label: '6 meses' };
    }
    
    return { parcial: false };
  };

  const linhasDRE = [
    { label: 'Receita Bruta', campo: 'receitaBruta' as keyof DREHistorico, tipo: 'valor' },
    { label: '(-) Impostos s/ Vendas', campo: 'impostosVendas' as keyof DREHistorico, tipo: 'valor', negativo: true },
    { label: 'Receita Líquida', campo: 'receitaLiquida' as keyof DREHistorico, tipo: 'valor', destaque: true },
    { label: '(-) Custos Diretos', campo: 'custosDiretos' as keyof DREHistorico, tipo: 'valor', negativo: true },
    { label: 'Lucro Bruto', campo: 'lucroBruto' as keyof DREHistorico, tipo: 'valor' },
    { label: '   % Margem Bruta', campo: 'margens.bruta', tipo: 'percentual', indentado: true },
    { label: '(-) Despesas Operacionais', campo: 'despesasOperacionais' as keyof DREHistorico, tipo: 'valor', negativo: true },
    { label: 'EBITDA', campo: 'ebitda' as keyof DREHistorico, tipo: 'valor', destaque: true },
    { label: '   % Margem EBITDA', campo: 'margens.ebitda', tipo: 'percentual', indentado: true },
    { label: '(-) Depreciação', campo: 'depreciacao' as keyof DREHistorico, tipo: 'valor', negativo: true },
    { label: 'EBIT', campo: 'ebit' as keyof DREHistorico, tipo: 'valor' },
    { label: '   % Margem EBIT', campo: 'margens.ebit', tipo: 'percentual', indentado: true },
    { label: '(-) Impostos s/ Lucro', campo: 'impostosLucro' as keyof DREHistorico, tipo: 'valor', negativo: true },
    { label: 'Lucro Líquido', campo: 'lucroLiquido' as keyof DREHistorico, tipo: 'valor', destaque: true },
    { label: '   % Margem Líquida', campo: 'margens.liquida', tipo: 'percentual', indentado: true },
  ];

  const getValor = (dre: DREHistorico, campo: string): number => {
    if (campo.includes('.')) {
      const [obj, prop] = campo.split('.');
      if (obj === 'margens') {
        return dre.margens[prop as keyof typeof dre.margens];
      }
    }
    return dre[campo as keyof DREHistorico] as number;
  };

  return (
    <Box sx={{ position: 'relative', mt: 2 }}>
      {/* Sombras dinâmicas de scroll (FASE 5.2) */}
      {showLeftShadow && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 24,
            background: 'linear-gradient(to right, rgba(0,0,0,0.12), transparent)',
            pointerEvents: 'none',
            zIndex: 3,
          }}
        />
      )}
      
      {showRightShadow && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 24,
            background: 'linear-gradient(to left, rgba(0,0,0,0.12), transparent)',
            pointerEvents: 'none',
            zIndex: 3,
          }}
        />
      )}
      
      <TableContainer 
        component={Paper}
        ref={containerRef}
        sx={{ 
          maxHeight: { xs: 500, md: 600 }, // Menor em mobile
          overflowX: 'auto',
          overflowY: 'auto',
          position: 'relative',
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
        }}
      >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell 
              sx={{ 
                fontWeight: 'bold',
                // Sticky header styling
                position: 'sticky',
                top: 0,
                zIndex: 2,
                bgcolor: 'background.paper',
                borderBottom: '2px solid',
                borderColor: 'divider',
              }}
            >
              Linha DRE
            </TableCell>
            {anos.map(ano => {
              const parcialInfo = isDadoParcial(ano);
              return (
                <TableCell 
                  key={ano} 
                  align="right" 
                  sx={{ 
                    fontWeight: 'bold',
                    // Sticky header styling
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    bgcolor: 'background.paper',
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                    {ano}
                    {parcialInfo.parcial && (
                      <Tooltip title={`Dados parciais: ${parcialInfo.label}`} arrow>
                        <Chip 
                          label={parcialInfo.label} 
                          size="small" 
                          color="warning"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.65rem', 
                            height: '18px',
                            '& .MuiChip-label': { px: 0.5 }
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {linhasDRE.map((linha, idx) => (
            <TableRow
              key={idx}
              sx={{
                // 🎨 ZEBRA STRIPING: Alternating row colors
                bgcolor: linha.destaque 
                  ? 'action.hover' 
                  : idx % 2 === 0 
                    ? 'background.paper' 
                    : 'action.hover',
                '&:hover': { bgcolor: 'action.selected' },
              }}
            >
              <TableCell
                sx={{
                  fontWeight: linha.destaque ? 'bold' : 'normal',
                  pl: linha.indentado ? 4 : 2,
                  fontStyle: linha.indentado ? 'italic' : 'normal',
                  color: linha.negativo ? 'text.secondary' : 'inherit',
                }}
              >
                {linha.label}
              </TableCell>
              {anos.map(ano => {
                const dre = empresa.anos[ano];
                const valor = getValor(dre, linha.campo);
                const valorFormatado = linha.tipo === 'percentual'
                  ? formatPercent(valor as number)
                  : formatCurrency(valor as number);

                // 🆕 Verificar se célula foi editada (apenas para campos de valor, não percentuais)
                const editInfo = linha.tipo === 'valor' && !linha.campo.includes('.')
                  ? getCellEditInfo(empresaKey, ano, linha.campo as keyof DREHistorico)
                  : { isEdited: false, originalValue: null, currentValue: valor as number, difference: 0, percentageChange: 0 };

                // Determinar cor: vermelho se prejuízo real (não apenas linha negativa)
                const isPrejuizo = !linha.negativo && (valor as number) < 0;
                const isLinhaNegativa = linha.negativo && valor !== 0;

                // TooltipAuditavel para Lucro Líquido 2023 (ERRO 1)
                const isLucroLiquido2023 = linha.campo === 'lucroLiquido' && ano === 2023;
                const isEBITDA2023 = linha.campo === 'ebitda' && ano === 2023;

                const cellContent = (
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: linha.destaque ? 'bold' : 'normal',
                      cursor: editInfo.isEdited ? 'help' : 'default',
                      borderBottom: '1px dashed',
                      borderColor: 'divider',
                      color: isPrejuizo ? 'error.main' : (linha.negativo ? 'text.secondary' : 'inherit'),
                      // 🎨 Destaque visual para células editadas
                      bgcolor: editInfo.isEdited ? 'warning.lighter' : 'inherit',
                      position: 'relative',
                      '&:hover': editInfo.isEdited ? {
                        bgcolor: 'warning.light',
                      } : {},
                    }}
                  >
                    {/* 🆕 Ícone de lápis para células editadas */}
                    {editInfo.isEdited && (
                      <Box
                        component="span"
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          fontSize: '0.65rem',
                          color: 'warning.dark',
                          opacity: 0.7,
                        }}
                      >
                        <EditIcon sx={{ fontSize: 10 }} />
                      </Box>
                    )}
                    
                    {/* Linhas negativas (despesas): (valor) em parênteses */}
                    {isLinhaNegativa && '('}
                    
                    {/* Valores: mostrar sinal - apenas se prejuízo real */}
                    {linha.tipo === 'percentual' 
                      ? formatPercent(valor as number)
                      : isLinhaNegativa
                        ? Math.abs(valor as number).toLocaleString('pt-BR', { maximumFractionDigits: 0 })
                        : (valor as number).toLocaleString('pt-BR', { 
                            maximumFractionDigits: 0,
                            signDisplay: isPrejuizo ? 'always' : 'auto'
                          })
                    }
                    
                    {isLinhaNegativa && ')'}
                  </TableCell>
                );

                // 🆕 Tooltip de edição (se célula foi editada)
                const cellWithEditTooltip = editInfo.isEdited ? (
                  <Tooltip
                    key={ano}
                    title={
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          Valor Editado
                        </Typography>
                        <Divider sx={{ my: 0.5, bgcolor: 'rgba(255,255,255,0.2)' }} />
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Valor Original:</strong> {formatCurrency(editInfo.originalValue || 0)}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Valor Editado:</strong> {formatCurrency(editInfo.currentValue)}
                        </Typography>
                        <Divider sx={{ my: 0.5, bgcolor: 'rgba(255,255,255,0.2)' }} />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: editInfo.difference > 0 ? 'success.light' : 'error.light'
                          }}
                        >
                          Diferença: {editInfo.difference > 0 ? '+' : ''}{formatCurrency(editInfo.difference)} 
                          ({editInfo.percentageChange > 0 ? '+' : ''}{editInfo.percentageChange.toFixed(1)}%)
                        </Typography>
                      </Box>
                    }
                    arrow
                    placement="left"
                  >
                    {cellContent}
                  </Tooltip>
                ) : cellContent;

                // Tooltip auditável para LL 2023 (tem precedência sobre edição)
                if (isLucroLiquido2023) {
                  return (
                    <TooltipAuditavel
                      key={ano}
                      titulo="Lucro Líquido 2023 - Sétima"
                      formula="Resultado antes IR - (CSLL + IRPJ)"
                      fonte={{
                        planilha: '03_3_DRE_Setima.xlsx',
                        aba: 'DRE_Anual',
                        celula: 'F25',
                        observacao: 'Prejuízo confirmado por análise fiscal'
                      }}
                      breakdown={[
                        { label: 'Resultado antes IR', valor: 71373, tipo: 'positivo' },
                        { label: '(-) CSLL (9%)', valor: -245046, tipo: 'negativo' },
                        { label: '(-) IRPJ (25%)', valor: -361473, tipo: 'negativo' },
                        { label: '= Lucro Líquido', valor: -535146, destaque: true, tipo: 'negativo' },
                      ]}
                      premissas={[
                        'Impostos sobre lucro presumido (base de cálculo sobre receita)',
                        'Erro anterior: +R$ 108k → Corrigido: -R$ 535k (ERRO 1)',
                      ]}
                      status="error"
                      placement="left"
                    >
                      {cellContent}
                    </TooltipAuditavel>
                  );
                }

                // Tooltip auditável para EBITDA 2023 (tem precedência sobre edição)
                if (isEBITDA2023) {
                  return (
                    <TooltipAuditavel
                      key={ano}
                      titulo="EBITDA 2023 - Sétima"
                      formula="Resultado Operacional + Depreciação + Amortização"
                      fonte={{
                        planilha: '03_3_DRE_Setima.xlsx',
                        aba: 'DRE_Anual',
                        celula: 'F18',
                      }}
                      breakdown={[
                        { label: 'Resultado Operacional', valor: 71373 },
                        { label: '(+) Depreciação', valor: 0 },
                        { label: '(+) Amortização', valor: 8129 },
                        { label: '= EBITDA', valor: 79502, destaque: true, tipo: 'positivo' },
                      ]}
                      premissas={[
                        'EBITDA positivo apesar do prejuízo líquido',
                        'Margem EBITDA: 1,21% (muito baixa para o setor)',
                      ]}
                      status="warning"
                      placement="left"
                    >
                      {cellContent}
                    </TooltipAuditavel>
                  );
                }

                // Se célula foi editada, mostrar tooltip de edição
                if (editInfo.isEdited) {
                  return cellWithEditTooltip;
                }

                // Tooltip padrão para células não editadas
                return (
                  <Tooltip
                    key={ano}
                    title={`${linha.label} ${ano}: ${valorFormatado} | Base para projeções`}
                    arrow
                    sx={{
                      '& .MuiTooltip-tooltip': {
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                      },
                      '& .MuiTooltip-arrow': {
                        color: 'background.paper',
                      },
                    }}
                  >
                    {cellContent}
                  </Tooltip>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    </Box>
  );
};

/**
 * Tabela DRE Comparativa (Lado a Lado)
 * Exibe Sétima e 555 Studios lado a lado para facilitar comparação
 */
interface TabelaDREComparativaProps {
  setima: HistoricoEmpresa;
  studio555: HistoricoEmpresa;
}

const TabelaDREComparativa: React.FC<TabelaDREComparativaProps> = ({ setima, studio555 }) => {
  const { containerRef, showLeftShadow, showRightShadow } = useHorizontalScroll();
  
  // Obter todos os anos de ambas as empresas
  const todosAnos = new Set([
    ...Object.keys(setima.anos).map(Number),
    ...Object.keys(studio555.anos).map(Number),
  ]);
  const anos = Array.from(todosAnos).sort();

  const linhasDRE = [
    { label: 'Receita Bruta', campo: 'receitaBruta' as keyof DREHistorico, tipo: 'valor' },
    { label: '(-) Impostos s/ Vendas', campo: 'impostosVendas' as keyof DREHistorico, tipo: 'valor', negativo: true },
    { label: 'Receita Líquida', campo: 'receitaLiquida' as keyof DREHistorico, tipo: 'valor', destaque: true },
    { label: '(-) Custos Diretos', campo: 'custosDiretos' as keyof DREHistorico, tipo: 'valor', negativo: true },
    { label: 'Lucro Bruto', campo: 'lucroBruto' as keyof DREHistorico, tipo: 'valor' },
    { label: '   % Margem Bruta', campo: 'margens.bruta', tipo: 'percentual', indentado: true },
    { label: '(-) Despesas Operacionais', campo: 'despesasOperacionais' as keyof DREHistorico, tipo: 'valor', negativo: true },
    { label: 'EBITDA', campo: 'ebitda' as keyof DREHistorico, tipo: 'valor', destaque: true },
    { label: '   % Margem EBITDA', campo: 'margens.ebitda', tipo: 'percentual', indentado: true },
    { label: '(-) Depreciação', campo: 'depreciacao' as keyof DREHistorico, tipo: 'valor', negativo: true },
    { label: 'EBIT', campo: 'ebit' as keyof DREHistorico, tipo: 'valor' },
    { label: '   % Margem EBIT', campo: 'margens.ebit', tipo: 'percentual', indentado: true },
    { label: '(-) Impostos s/ Lucro', campo: 'impostosLucro' as keyof DREHistorico, tipo: 'valor', negativo: true },
    { label: 'Lucro Líquido', campo: 'lucroLiquido' as keyof DREHistorico, tipo: 'valor', destaque: true },
    { label: '   % Margem Líquida', campo: 'margens.liquida', tipo: 'percentual', indentado: true },
  ];

  const getValor = (dre: DREHistorico | undefined, campo: string): number => {
    if (!dre) return 0;
    if (campo.includes('.')) {
      const [obj, prop] = campo.split('.');
      if (obj === 'margens') {
        return dre.margens[prop as keyof typeof dre.margens];
      }
    }
    return dre[campo as keyof DREHistorico] as number;
  };

  return (
    <Box sx={{ position: 'relative', mt: 2 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Modo Comparação:</strong> Visualize a DRE da Sétima e 555 Studios lado a lado para identificar diferenças, sinergias e oportunidades de consolidação.
        </Typography>
      </Alert>

      {/* Sombras de scroll */}
      {showLeftShadow && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 24,
            background: 'linear-gradient(to right, rgba(0,0,0,0.12), transparent)',
            pointerEvents: 'none',
            zIndex: 3,
          }}
        />
      )}
      
      {showRightShadow && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 24,
            background: 'linear-gradient(to left, rgba(0,0,0,0.12), transparent)',
            pointerEvents: 'none',
            zIndex: 3,
          }}
        />
      )}

      <TableContainer 
        component={Paper}
        ref={containerRef}
        sx={{ 
          maxHeight: { xs: 500, md: 600 },
          overflowX: 'auto',
          overflowY: 'auto',
          position: 'relative',
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ 
                  fontWeight: 'bold',
                  position: 'sticky',
                  left: 0,
                  top: 0,
                  zIndex: 5, // Maior que o z-index dos headers normais (2) e das células (3)
                  bgcolor: 'background.paper',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  minWidth: 200,
                  // Garante que o conteúdo role por baixo sem transparência
                  boxShadow: '2px 2px 4px rgba(0,0,0,0.1)',
                  borderRight: '1px solid',
                  borderRightColor: 'divider',
                }}
              >
                Linha DRE
              </TableCell>
              {anos.map(ano => (
                <React.Fragment key={ano}>
                  <TableCell 
                    align="right"
                    sx={{ 
                      fontWeight: 'bold',
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      bgcolor: 'primary.lighter',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      borderLeft: '2px solid',
                      borderLeftColor: 'divider',
                    }}
                  >
                    <Box>
                      <Typography variant="caption" display="block" sx={{ color: 'primary.dark' }}>
                        Sétima
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {ano}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell 
                    align="right"
                    sx={{ 
                      fontWeight: 'bold',
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      bgcolor: 'secondary.lighter',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box>
                      <Typography variant="caption" display="block" sx={{ color: 'secondary.dark' }}>
                        555 Studios
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {ano}
                      </Typography>
                    </Box>
                  </TableCell>
                </React.Fragment>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {linhasDRE.map((linha, idx) => (
              <TableRow
                key={idx}
                sx={{
                  bgcolor: linha.destaque 
                    ? 'action.hover' 
                    : idx % 2 === 0 
                      ? 'background.paper' 
                      : 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' },
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: linha.destaque ? 'bold' : 'normal',
                    pl: linha.indentado ? 4 : 2,
                    fontStyle: linha.indentado ? 'italic' : 'normal',
                    color: linha.negativo ? 'text.secondary' : 'inherit',
                    position: 'sticky',
                    left: 0,
                    bgcolor: linha.destaque 
                      ? 'action.hover' 
                      : idx % 2 === 0 
                        ? 'background.paper' 
                        : 'action.hover',
                    zIndex: 3,
                    // Garante que o conteúdo role por baixo sem transparência
                    boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                    borderRight: '1px solid',
                    borderRightColor: 'divider',
                  }}
                >
                  {linha.label}
                </TableCell>
                {anos.map(ano => {
                  const dreSetima = setima.anos[ano];
                  const dreStudio555 = studio555.anos[ano];
                  
                  const valorSetima = getValor(dreSetima, linha.campo);
                  const valorStudio555 = getValor(dreStudio555, linha.campo);

                  const isLinhaNegativa = linha.negativo;

                  return (
                    <React.Fragment key={ano}>
                      {/* Célula Sétima */}
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: linha.destaque ? 'bold' : 'normal',
                          color: (!isLinhaNegativa && valorSetima < 0) ? 'error.main' : (isLinhaNegativa ? 'text.secondary' : 'inherit'),
                          bgcolor: 'primary.lighter',
                          opacity: dreSetima ? 1 : 0.5,
                          borderLeft: '2px solid',
                          borderLeftColor: 'divider',
                        }}
                      >
                        {dreSetima ? (
                          <>
                            {isLinhaNegativa && valorSetima !== 0 && '('}
                            {linha.tipo === 'percentual' 
                              ? formatPercent(valorSetima)
                              : isLinhaNegativa
                                ? Math.abs(valorSetima).toLocaleString('pt-BR', { maximumFractionDigits: 0 })
                                : valorSetima.toLocaleString('pt-BR', { maximumFractionDigits: 0, signDisplay: valorSetima < 0 ? 'always' : 'auto' })
                            }
                            {isLinhaNegativa && valorSetima !== 0 && ')'}
                          </>
                        ) : '-'}
                      </TableCell>

                      {/* Célula 555 Studios */}
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: linha.destaque ? 'bold' : 'normal',
                          color: (!isLinhaNegativa && valorStudio555 < 0) ? 'error.main' : (isLinhaNegativa ? 'text.secondary' : 'inherit'),
                          bgcolor: 'secondary.lighter',
                          opacity: dreStudio555 ? 1 : 0.5,
                        }}
                      >
                        {dreStudio555 ? (
                          <>
                            {isLinhaNegativa && valorStudio555 !== 0 && '('}
                            {linha.tipo === 'percentual' 
                              ? formatPercent(valorStudio555)
                              : isLinhaNegativa
                                ? Math.abs(valorStudio555).toLocaleString('pt-BR', { maximumFractionDigits: 0 })
                                : valorStudio555.toLocaleString('pt-BR', { maximumFractionDigits: 0, signDisplay: valorStudio555 < 0 ? 'always' : 'auto' })
                            }
                            {isLinhaNegativa && valorStudio555 !== 0 && ')'}
                          </>
                        ) : '-'}
                      </TableCell>
                    </React.Fragment>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

/**
 * Gráfico de Evolução de Receita
 */
interface GraficoReceitaProps {
  setima: HistoricoEmpresa;
  studio555: HistoricoEmpresa;
  isMobile?: boolean;
}

const GraficoReceita: React.FC<GraficoReceitaProps> = ({ setima, studio555, isMobile = false }) => {
  const todosAnos = new Set([
    ...Object.keys(setima.anos).map(Number),
    ...Object.keys(studio555.anos).map(Number),
  ]);
  const anosOrdenados = Array.from(todosAnos).sort();

  const dados = anosOrdenados.map(ano => ({
    ano: ano.toString(),
    Sétima: setima.anos[ano]?.receitaBruta || 0,
    '555 Studios': studio555.anos[ano]?.receitaBruta || 0,
    Total: (setima.anos[ano]?.receitaBruta || 0) + (studio555.anos[ano]?.receitaBruta || 0),
  }));

  // TAREFA 2.3: Estado para controlar visibilidade das séries
  const [visibleSeries, setVisibleSeries] = React.useState<Record<string, boolean>>({
    'Sétima': true,
    '555 Studios': true,
    'Total': true,
  });

  // TAREFA 2.3: Toggle de visibilidade ao clicar na legenda
  const handleLegendClick = (dataKey: string) => {
    setVisibleSeries(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey],
    }));
  };

  return (
    <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
      <LineChart data={dados}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="ano" />
        <YAxis
          tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`}
        />
        <RechartsTooltip
          content={
            <CustomChartTooltip
              valueFormatter={(value) => formatCurrency(value)}
              labelFormatter={(label) => `Ano ${label}`}
            />
          }
        />
        <Legend 
          onClick={(e) => e.dataKey && handleLegendClick(e.dataKey as string)}
          wrapperStyle={{ cursor: 'pointer' }}
        />
        {visibleSeries['Sétima'] && (
          <Line type="monotone" dataKey="Sétima" stroke={designTokens.colors.companies.setima} strokeWidth={2} />
        )}
        {visibleSeries['555 Studios'] && (
          <Line type="monotone" dataKey="555 Studios" stroke={designTokens.colors.companies.studios555} strokeWidth={2} />
        )}
        {visibleSeries['Total'] && (
          <Line type="monotone" dataKey="Total" stroke={designTokens.colors.companies.consolidated} strokeWidth={3} strokeDasharray="5 5" />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};

/**
 * Gráfico de Evolução de Margens
 */
interface GraficoMargensProps {
  empresa: HistoricoEmpresa;
  isMobile?: boolean;
}

const GraficoMargens: React.FC<GraficoMargensProps> = ({ empresa, isMobile = false }) => {
  const anos = Object.keys(empresa.anos).map(Number).sort();

  const dados = anos.map(ano => {
    const dre = empresa.anos[ano];
    return {
      ano: ano.toString(),
      'Margem Bruta': dre.margens.bruta,
      'Margem EBITDA': dre.margens.ebitda,
      'Margem Líquida': dre.margens.liquida,
    };
  });

  // TAREFA 2.3: Estado para controlar visibilidade das séries (margens)
  const [visibleSeries, setVisibleSeries] = React.useState<Record<string, boolean>>({
    'Margem Bruta': true,
    'Margem EBITDA': true,
    'Margem Líquida': true,
  });

  // TAREFA 2.3: Toggle de visibilidade ao clicar na legenda (margens)
  const handleLegendClick = (dataKey: string) => {
    setVisibleSeries(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey],
    }));
  };

  return (
    <ResponsiveContainer width="100%" height={isMobile ? 220 : 250}>
      <BarChart data={dados}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="ano" />
        <YAxis tickFormatter={(value) => `${value}%`} />
        <RechartsTooltip 
          content={
            <CustomChartTooltip
              valueFormatter={(value) => formatPercent(value)}
              labelFormatter={(label) => `Ano ${label}`}
            />
          }
        />
        <Legend 
          onClick={(e) => e.dataKey && handleLegendClick(e.dataKey as string)}
          wrapperStyle={{ cursor: 'pointer' }}
        />
        {visibleSeries['Margem Bruta'] && (
          <Bar dataKey="Margem Bruta" fill={designTokens.colors.companies.consolidated} />
        )}
        {visibleSeries['Margem EBITDA'] && (
          <Bar dataKey="Margem EBITDA" fill={designTokens.colors.companies.setima} />
        )}
        {visibleSeries['Margem Líquida'] && (
          <Bar dataKey="Margem Líquida" fill={designTokens.colors.warning.main} />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
};

/**
 * Componente Principal
 */
export const HistoricoFinanceiro: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [empresaSelecionada, setEmpresaSelecionada] = useState<'setima' | 'studio555' | 'consolidado'>('setima');
  const [modoComparacao, setModoComparacao] = useState<boolean>(false); // 🆕 Modo de comparação lado a lado
  
  // Usar dados integrados que respeitam edições do DREStore
  const dados = useDadosHistoricosIntegrado();
  
  // 🟡 REMOVIDO: variáveis não mais utilizadas após remoção do EditedDataAlert
  // const { hasEdits, restoreToOriginal } = useDREIntegration();

  const empresaAtual = empresaSelecionada === 'setima' ? dados.setima : dados.studio555;
  
  // 🆕 Calcular período dinâmico baseado nos dados reais
  const anosConsolidado = Object.keys(dados.consolidado.anos).map(Number).sort();
  const anoInicial = anosConsolidado[0] || 2020;
  const anoFinal = anosConsolidado[anosConsolidado.length - 1] || 2024;

  // 🆕 Detectar depreciação zero em anos recentes
  const depreciacaoZeroWarnings = React.useMemo(() => {
    const warnings: Array<{ empresa: string; ano: number; sugestao: number }> = [];
    
    // Verificar Sétima
    Object.entries(dados.setima.anos).forEach(([ano, dre]) => {
      const anoNum = Number(ano);
      if (anoNum >= 2024 && dre.depreciacao === 0 && dre.receitaBruta > 0) {
        warnings.push({
          empresa: 'Sétima',
          ano: anoNum,
          sugestao: dre.receitaBruta * 0.03, // 3%
        });
      }
    });
    
    // Verificar 555 Studios
    Object.entries(dados.studio555.anos).forEach(([ano, dre]) => {
      const anoNum = Number(ano);
      if (anoNum >= 2024 && dre.depreciacao === 0 && dre.receitaBruta > 0) {
        warnings.push({
          empresa: '555 Studios',
          ano: anoNum,
          sugestao: dre.receitaBruta * 0.02, // 2%
        });
      }
    });
    
    return warnings;
  }, [dados]);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink component={Link} to="/" color="inherit" sx={{ display: 'flex', alignItems: 'center' }}>
          <Home sx={{ mr: 0.5 }} fontSize="small" />
          Dashboard
        </MuiLink>
        <Typography color="text.primary">Dados históricos</Typography>
      </Breadcrumbs>

      {/* ✅ MOVIDO: ContextPanel agora está integrado no AppHeader (global em todas as páginas) */}

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ShowChartIcon sx={{ fontSize: 36 }} />
          Dados históricos e base de análise
        </Typography>
        <Typography variant="body1" color="text.secondary">
          DREs consolidadas de {anoInicial} a {anoFinal}, métricas-chave e fundamentos das projeções.
        </Typography>
      </Box>

      {/* 🟡 REMOVIDO: Alert redundante - ContextPanel já informa sobre edições */}
      
      {/* 🆕 Alert de Depreciação Zero */}
      {depreciacaoZeroWarnings.length > 0 && (
        <Alert severity="warning" icon={<InfoIcon sx={{ fontSize: 20 }} />} sx={{ mb: 3 }}>
          <Typography variant="body2" gutterBottom>
            <strong><WarningIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />Depreciação zero detectada:</strong> Os seguintes anos têm depreciação zero, o que pode distorcer o cálculo de EBIT e valuation:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
            {depreciacaoZeroWarnings.map((w, idx) => (
              <li key={idx}>
                <Typography variant="body2">
                  <strong>{w.empresa} {w.ano}:</strong> Sugerido {formatCurrency(w.sugestao)} (3% da receita)
                </Typography>
              </li>
            ))}
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <LightbulbIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} /><strong>Dica:</strong> Edite na página <Link to="/dre-editavel" style={{ color: 'inherit', textDecoration: 'underline' }}>DRE Editável</Link> para corrigir.
          </Typography>
        </Alert>
      )}

      {/* Alert de Auditabilidade */}
      <Alert severity="info" icon={<DashboardIcon sx={{ fontSize: 20 }} />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Auditabilidade:</strong> Projeções derivadas destes dados históricos. Clique em um valor para ver a origem e a fórmula. <SearchIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />
        </Typography>
      </Alert>

      {/* KPIs Consolidados */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TrendingUpIconMui sx={{ fontSize: 24 }} />
        Visão Geral {anoInicial}-{anoFinal}
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <KPICard
            title="Receita total acumulada"
            value={formatCurrency(
              Object.values(dados.consolidado.anos).reduce((sum, ano) => sum + ano.receitaTotal, 0)
            )}
            subtitle={`Soma da receita bruta anual de Sétima e 555 Studios (${anoInicial}-${anoFinal})`}
            color="primary"
            description="Soma de todas as receitas brutas geradas pelas duas empresas ao longo do período histórico analisado."
            interpretation={
              Object.values(dados.consolidado.anos).reduce((sum, ano) => sum + ano.receitaTotal, 0) > 30000000
                ? "Volume sólido"
                : "Em crescimento"
            }
            formula={`∑ (Receita Sétima + Receita 555) de ${anoInicial} a ${anoFinal}`}
            context={`Este valor representa o faturamento bruto total acumulado. Importante: 555 Studios iniciou operações apenas em ${dados.studio555.metricas.periodoInicio === 2023 ? 'jul/2023' : dados.studio555.metricas.periodoInicio}, então contribui para o período de ${dados.studio555.metricas.periodoInicio} a ${dados.studio555.metricas.periodoFim}.`}
            tooltip="Receita acumulada total do grupo no período histórico. Base fundamental para validar projeções futuras."
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <KPICard
            title={`CAGR - Crescimento composto Sétima (${dados.setima.metricas.periodoInicio} a ${dados.setima.metricas.periodoFim})`}
            value={formatPercent(dados.setima.metricas.cagr.receita * 100)}
            subtitle="Taxa anual composta da receita da Sétima no período"
            color="success"
            description="Taxa de crescimento anual composta da receita da Sétima, considerando o efeito de capitalização ao longo de 4 anos."
            interpretation={
              dados.setima.metricas.cagr.receita > 0.10 
                ? "Crescimento forte" 
                : dados.setima.metricas.cagr.receita > 0.05
                ? "Crescimento moderado"
                : "Crescimento baixo"
            }
            formula="CAGR = (Receita Final / Receita Inicial)^(1/anos) - 1"
            context={`Sétima cresceu de ${formatCurrency(dados.setima.anos[dados.setima.metricas.periodoInicio]?.receitaBruta || 0)} (${dados.setima.metricas.periodoInicio}) para ${formatCurrency(dados.setima.anos[dados.setima.metricas.periodoFim]?.receitaBruta || 0)} (${dados.setima.metricas.periodoFim}). Note a queda em 2023 devido a fatores específicos do mercado.`}
            tooltip="Taxa de crescimento anual composta - métrica chave para projetar crescimento futuro de forma conservadora."
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <KPICard
            title={`CAGR - Crescimento anualizado 555 Studios (${dados.studio555.metricas.periodoInicio === 2023 ? 'jul 2023' : dados.studio555.metricas.periodoInicio} a ${dados.studio555.metricas.periodoFim})`}
            value={formatPercent(dados.studio555.metricas.cagr.receita * 100)}
            subtitle={dados.studio555.metricas.periodoInicio === 2023 ? "Base parcial em 2023 com anualização" : `Crescimento de ${dados.studio555.metricas.periodoInicio} a ${dados.studio555.metricas.periodoFim}`}
            color="secondary"
            description={`Taxa de crescimento explosivo da 555 Studios desde o início das operações em ${dados.studio555.metricas.periodoInicio === 2023 ? 'julho/2023' : dados.studio555.metricas.periodoInicio} até ${dados.studio555.metricas.periodoFim}.`}
            interpretation="Hypergrowth típico de startup"
            formula={`CAGR = (Receita ${dados.studio555.metricas.periodoFim} / Receita ${dados.studio555.metricas.periodoInicio})^(1/${dados.studio555.metricas.periodoFim - dados.studio555.metricas.periodoInicio}) - 1`}
            context={`555 Studios é uma startup em fase de crescimento acelerado. ${dados.studio555.metricas.periodoInicio === 2023 ? `Iniciou em jul/2023 com ${formatCurrency(dados.studio555.anos[2023]?.receitaBruta || 0)} (6 meses)` : `Iniciou em ${dados.studio555.metricas.periodoInicio}`} e alcançou ${formatCurrency(dados.studio555.anos[dados.studio555.metricas.periodoFim]?.receitaBruta || 0)} em ${dados.studio555.metricas.periodoFim}${dados.studio555.metricas.periodoInicio === 2023 && dados.studio555.metricas.periodoFim === 2024 ? ' (12 meses)' : ''}. Crescimento característico de novas operações.`}
            tooltip="Crescimento muito acelerado - comum em startups. Premissas de crescimento futuro devem considerar maturação gradual."
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <KPICard
            title="Margem EBITDA média"
            value={formatPercent(dados.consolidado.metricas.medias.margemEbitda)}
            subtitle={`Média consolidada de ${anoInicial} a ${anoFinal}`}
            color="warning"
            description="Média histórica da margem EBITDA consolidada, indicando a capacidade de geração de caixa operacional do grupo."
            interpretation={
              dados.consolidado.metricas.medias.margemEbitda > 20
                ? "Rentabilidade alta"
                : dados.consolidado.metricas.medias.margemEbitda > 15
                ? "Rentabilidade saudável"
                : "Em melhora"
            }
            formula="Margem EBITDA = (EBITDA / Receita Líquida) × 100"
            context={`Margem média de ${formatPercent(dados.consolidado.metricas.medias.margemEbitda)} indica boa eficiência operacional. Sétima teve margens mais baixas em anos anteriores (fase de investimento), enquanto 555 Studios já inicia com margens elevadas (modelo de negócio escalável).`}
            tooltip="Margem EBITDA média histórica - referência para validar premissas de margens nas projeções. Target: 18% nos próximos 5 anos."
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Gráfico de Evolução de Receita */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TrendingDownIconMui sx={{ fontSize: 24 }} />
        Evolução da receita por ano
      </Typography>
      
      {/* TAREFA 2.3: Instrução sobre interatividade da legenda */}
      <Alert severity="info" icon={<ShowChartIcon sx={{ fontSize: 20 }} />} sx={{ mb: 2 }}>
        <Typography variant="body2">
          <LightbulbIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} /><strong>Dica:</strong> Clique nos itens da legenda para ocultar/exibir séries e focar na análise que interessa.
        </Typography>
      </Alert>
      
      <Paper sx={{ p: 3, mb: 4 }}>
        <GraficoReceita setima={dados.setima} studio555={dados.studio555} isMobile={isMobile} />
      </Paper>

      <Divider sx={{ my: 4 }} />

      {/* Seletor de Empresa + Tabela DRE */}
      <Box sx={{ mb: 2, display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 2 }}>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DashboardIcon sx={{ fontSize: 24 }} />
          DRE Histórica Detalhada
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {/* Toggle: Modo de visualização */}
          <ToggleButtonGroup
            value={modoComparacao ? 'comparacao' : 'individual'}
            exclusive
            onChange={(_, newValue) => {
              if (newValue !== null) {
                setModoComparacao(newValue === 'comparacao');
              }
            }}
            size="small"
          >
            <ToggleButton value="individual">
              <DashboardIcon sx={{ fontSize: 18, mr: 1 }} />
              Individual
            </ToggleButton>
            <ToggleButton value="comparacao">
              <SearchIcon sx={{ fontSize: 18, mr: 1 }} />
              Comparar Lado a Lado
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Seletor de empresa (apenas no modo individual) */}
          {!modoComparacao && (
            <ToggleButtonGroup
              value={empresaSelecionada}
              exclusive
              onChange={(_, newValue) => newValue && setEmpresaSelecionada(newValue)}
              size="small"
            >
              <ToggleButton value="setima">
                <DashboardIcon sx={{ fontSize: 18, mr: 1 }} />
                Sétima
              </ToggleButton>
              <ToggleButton value="studio555">
                <ShowChartIcon sx={{ fontSize: 18, mr: 1 }} />
                555 Studios
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
      </Box>

      {/* Renderizar tabela apropriada baseada no modo */}
      {modoComparacao ? (
        <TabelaDREComparativa setima={dados.setima} studio555={dados.studio555} />
      ) : (
        empresaSelecionada !== 'consolidado' && <TabelaDRE empresa={empresaAtual} />
      )}

      {/* Gráfico de Margens */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <DashboardIcon sx={{ fontSize: 24 }} />
        {`Evolução de margens da ${empresaAtual.empresa}`}
      </Typography>
      <Paper sx={{ p: 3, mb: 4 }}>
        <GraficoMargens empresa={empresaAtual} isMobile={isMobile} />
      </Paper>

      <Divider sx={{ my: 4 }} />

      {/* Análise de Tendências - EXPANSÃO: Sétima + 555 + Consolidado */}
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <CalculateIcon sx={{ fontSize: 24 }} />
        Análise de Tendências Completa
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Análise comparativa de CAGR, médias e volatilidade para todas as empresas do grupo. 
          Permite identificar diferenças de performance e fundamentar projeções.
        </Typography>
      </Alert>

      {/* SÉTIMA */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssessmentIcon sx={{ fontSize: 24 }} />
        Sétima ({dados.setima.metricas.periodoInicio} - {dados.setima.metricas.periodoFim})
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                CAGR (Crescimento Composto)
              </Typography>
              {/* 🎯 TAREFA 3.3: Glossário de Termos */}
              <GlossaryTooltip
                term="CAGR (Compound Annual Growth Rate)"
                definition="Taxa de crescimento anual composta que mede o retorno médio de um investimento ao longo de um período, assumindo reinvestimento dos ganhos."
                formula="CAGR = (Valor Final / Valor Inicial)^(1/Anos) - 1"
                interpretation="Um CAGR de 15% significa que a métrica cresceu em média 15% ao ano no período. Valores positivos indicam crescimento, negativos indicam declínio."
              />
            </Box>
            <Stack spacing={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography>Receita:</Typography>
                <CAGRChip value={dados.setima.metricas.cagr.receita} />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography>EBITDA:</Typography>
                  <GlossaryTooltip
                    term="EBITDA"
                    definition="Earnings Before Interest, Taxes, Depreciation and Amortization. Representa o lucro operacional antes de despesas financeiras, impostos e depreciação."
                    formula="EBITDA = Lucro Operacional + Depreciação + Amortização"
                    interpretation="Mede a capacidade de geração de caixa operacional da empresa, eliminando efeitos de estrutura de capital e políticas contábeis."
                  />
                </Box>
                <CAGRChip value={dados.setima.metricas.cagr.ebitda} />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography>Lucro Líquido:</Typography>
                <CAGRChip value={dados.setima.metricas.cagr.lucroLiquido} />
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Médias Históricas
              </Typography>
            </Box>
            <Stack spacing={2}>
              <Box display="flex" justifyContent="space-between">
                <Typography>Margem Bruta:</Typography>
                <Typography fontWeight="bold">{formatPercent(dados.setima.metricas.medias.margemBruta)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Margem EBITDA:</Typography>
                <Typography fontWeight="bold">{formatPercent(dados.setima.metricas.medias.margemEbitda)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Margem Líquida:</Typography>
                <Typography fontWeight="bold">{formatPercent(dados.setima.metricas.medias.margemLiquida)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Receita Média Anual:</Typography>
                <Typography fontWeight="bold">{formatCurrency(dados.setima.metricas.medias.receita)}</Typography>
              </Box>
            </Stack>
            
            {/* 🎯 TAREFA 3.2: Benchmark de Mercado */}
            <Alert 
              severity={getBenchmarkChip(dados.setima.metricas.medias.margemEbitda).severity} 
              icon={<GpsFixedIcon sx={{ fontSize: 18 }} />}
              sx={{ mt: 2 }}
            >
              <Typography variant="caption">
                <strong>Benchmark:</strong> {getBenchmarkChip(dados.setima.metricas.medias.margemEbitda).text}
              </Typography>
            </Alert>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Volatilidade (Desvio Padrão)
              </Typography>
              {/* 🎯 TAREFA 3.3: Glossário de Termos */}
              <GlossaryTooltip
                term="Volatilidade (Desvio Padrão)"
                definition="Medida estatística que quantifica a dispersão dos valores históricos em torno da média. Indica o grau de variabilidade dos resultados financeiros ao longo do tempo."
                formula="σ = √[Σ(x - μ)² / n]"
                interpretation="Alta volatilidade sugere resultados imprevisíveis; baixa volatilidade indica estabilidade operacional. Útil para avaliar risco em projeções futuras."
              />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography>Receita: {formatCurrency(dados.setima.metricas.volatilidade.receita)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography>Margem EBITDA: {formatPercent(dados.setima.metricas.volatilidade.margemEbitda)} pp</Typography>
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="caption">
                Volatilidade {dados.setima.metricas.volatilidade.receita / dados.setima.metricas.medias.receita < 0.2 ? 'baixa' : 'alta'}. 
                Base histórica {dados.setima.metricas.volatilidade.receita / dados.setima.metricas.medias.receita < 0.2 ? 'estável' : 'instável'} 
                para projeções ({dados.setima.metricas.anosAnalise} anos analisados)
              </Typography>
            </Alert>
          </Paper>
        </Grid>
      </Grid>

      {/* 555 STUDIOS */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, color: 'secondary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
        <RocketIcon sx={{ fontSize: 24 }} />
        555 Studios ({dados.studio555.metricas.periodoInicio} - {dados.studio555.metricas.periodoFim})
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <WarningIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} /><strong>Histórico limitado:</strong> Dados de apenas {dados.studio555.metricas.anosAnalise} anos 
          ({dados.studio555.metricas.periodoInicio === 2023 ? '2023 parcial (6 meses)' : dados.studio555.metricas.periodoInicio} a {dados.studio555.metricas.periodoFim}).
          Métricas de crescimento (CAGR) devem ser interpretadas com cautela. Dados anualizados para comparabilidade.
        </Typography>
      </Alert>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              CAGR (Crescimento Composto)
            </Typography>
            <Stack spacing={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography>Receita:</Typography>
                <CAGRChip value={dados.studio555.metricas.cagr.receita} />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography>EBITDA:</Typography>
                <CAGRChip value={dados.studio555.metricas.cagr.ebitda} />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography>Lucro Líquido:</Typography>
                <CAGRChip value={dados.studio555.metricas.cagr.lucroLiquido} />
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Médias Históricas
            </Typography>
            <Stack spacing={2}>
              <Box display="flex" justifyContent="space-between">
                <Typography>Margem Bruta:</Typography>
                <Typography fontWeight="bold">{formatPercent(dados.studio555.metricas.medias.margemBruta)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Margem EBITDA:</Typography>
                <Typography fontWeight="bold">{formatPercent(dados.studio555.metricas.medias.margemEbitda)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Margem Líquida:</Typography>
                <Typography fontWeight="bold">{formatPercent(dados.studio555.metricas.medias.margemLiquida)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Receita Média Anual:</Typography>
                <Typography fontWeight="bold">{formatCurrency(dados.studio555.metricas.medias.receita)}</Typography>
              </Box>
            </Stack>
            
            {/* 🎯 TAREFA 3.2: Benchmark de Mercado */}
            <Alert 
              severity={getBenchmarkChip(dados.studio555.metricas.medias.margemEbitda).severity} 
              icon={<GpsFixedIcon sx={{ fontSize: 18 }} />}
              sx={{ mt: 2 }}
            >
              <Typography variant="caption">
                <strong>Benchmark:</strong> {getBenchmarkChip(dados.studio555.metricas.medias.margemEbitda).text}
              </Typography>
            </Alert>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Volatilidade (Desvio Padrão)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography>Receita: {formatCurrency(dados.studio555.metricas.volatilidade.receita)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography>Margem EBITDA: {formatPercent(dados.studio555.metricas.volatilidade.margemEbitda)} pp</Typography>
              </Grid>
            </Grid>
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="caption">
                Volatilidade {dados.studio555.metricas.volatilidade.receita / dados.studio555.metricas.medias.receita < 0.2 ? 'baixa' : 'alta'}
                {' '}(base: {dados.studio555.metricas.anosAnalise} anos). 
                <strong> Startup em fase de crescimento acelerado - volatilidade esperada.</strong>
              </Typography>
            </Alert>
          </Paper>
        </Grid>
      </Grid>

      {/* CONSOLIDADO */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, color: 'success.main' }}>
        🏢 Grupo Consolidado ({dados.consolidado.metricas.periodoInicio} - {dados.consolidado.metricas.periodoFim})
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              CAGR (Crescimento Composto)
            </Typography>
            <Stack spacing={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography>Receita Total:</Typography>
                <CAGRChip value={dados.consolidado.metricas.cagr.receita} />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography>EBITDA Total:</Typography>
                <CAGRChip value={dados.consolidado.metricas.cagr.ebitda} />
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Médias Históricas
            </Typography>
            <Stack spacing={2}>
              <Box display="flex" justifyContent="space-between">
                <Typography>Margem EBITDA:</Typography>
                <Typography fontWeight="bold">{formatPercent(dados.consolidado.metricas.medias.margemEbitda)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography>Receita Média Anual:</Typography>
                <Typography fontWeight="bold">{formatCurrency(dados.consolidado.metricas.medias.receita)}</Typography>
              </Box>
            </Stack>
            
            {/* 🎯 TAREFA 3.2: Benchmark de Mercado */}
            <Alert 
              severity={getBenchmarkChip(dados.consolidado.metricas.medias.margemEbitda).severity} 
              icon={<GpsFixedIcon sx={{ fontSize: 18 }} />}
              sx={{ mt: 2 }}
            >
              <Typography variant="caption">
                <strong>Benchmark do Grupo:</strong> {getBenchmarkChip(dados.consolidado.metricas.medias.margemEbitda).text}
              </Typography>
            </Alert>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Volatilidade (Desvio Padrão)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography>Receita: {formatCurrency(dados.consolidado.metricas.volatilidade.receita)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography>Margem EBITDA: {formatPercent(dados.consolidado.metricas.volatilidade.margemEbitda)} pp</Typography>
              </Grid>
            </Grid>
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="caption">
                Performance consolidada do grupo ({dados.consolidado.metricas.anosAnalise} anos). 
                Volatilidade {dados.consolidado.metricas.volatilidade.receita / dados.consolidado.metricas.medias.receita < 0.2 ? 'controlada' : 'elevada'} 
                {' '}reflete mix entre operação madura (Sétima) e startup em crescimento (555).
              </Typography>
            </Alert>
          </Paper>
        </Grid>
      </Grid>

      {/* 🎯 TAREFA 3.1: Seção "Próximos Passos" - Cards de Ação Contextuais */}
      <Divider sx={{ my: 6 }} />
      
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <ArrowRight size={24} />
        Próximos Passos
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Com base nos dados históricos, siga o fluxo de trabalho para ajustar premissas e avaliar impactos no valuation.
        </Typography>
      </Alert>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Card 1: Ajustar Premissas */}
        <Grid item xs={12} md={4}>
          <Paper 
            sx={{ 
              p: 3, 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TrendingUpIcon color="primary" fontSize="large" />
              <Typography variant="h6" color="primary">
                1. Ajustar Premissas
              </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flex: 1 }}>
              Use os dados históricos como base para projeções futuras. 
              O sistema pré-preencherá crescimento médio (CAGR: {formatPercent(dados.consolidado.metricas.cagr.receita)}) 
              e margens ({formatPercent(dados.consolidado.metricas.medias.margemEbitda)} EBITDA).
            </Typography>
            
            <Button
              component={Link}
              to="/premissas"
              variant="contained"
              color="primary"
              fullWidth
              endIcon={<ArrowRight size={18} />}
              sx={touchFriendlyButton}
            >
              Ir para Premissas
            </Button>
          </Paper>
        </Grid>

        {/* Card 2: Ver Impacto no Valuation */}
        <Grid item xs={12} md={4}>
          <Paper 
            sx={{ 
              p: 3, 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ShowChartIcon color="secondary" fontSize="large" />
              <Typography variant="h6" color="secondary">
                2. Ver Impacto no Valuation
              </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flex: 1 }}>
              Após ajustar premissas, visualize cenários (pessimista, base, otimista) 
              e métricas-chave como TIR, Payback e valor justo por ação.
            </Typography>
            
            <Button
              component={Link}
              to="/dashboard"
              variant="contained"
              color="secondary"
              fullWidth
              endIcon={<ArrowRight size={18} />}
              sx={touchFriendlyButton}
            >
              Ir para Dashboard
            </Button>
          </Paper>
        </Grid>

        {/* Card 3: Exportar Análise */}
        <Grid item xs={12} md={4}>
          <Paper 
            sx={{ 
              p: 3, 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ArticleIcon color="success" fontSize="large" />
              <Typography variant="h6" color="success.main">
                3. Exportar Análise
              </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flex: 1 }}>
              Gere relatórios em CSV ou Excel para compartilhar com stakeholders 
              ou incluir em apresentações e documentos de due diligence.
            </Typography>
            
            <Stack spacing={1}>
              <Button
                variant="outlined"
                color="success"
                fullWidth
                startIcon={<Download />}
                onClick={() => alert('Exportação CSV em desenvolvimento - Sprint 4')}
                sx={{ ...touchFriendlyButton, minHeight: '36px' }}
              >
                Exportar CSV
              </Button>
              <Button
                variant="outlined"
                color="success"
                fullWidth
                startIcon={<Download />}
                onClick={() => alert('Exportação Excel em desenvolvimento - Sprint 4')}
                sx={{ ...touchFriendlyButton, minHeight: '36px' }}
              >
                Exportar Excel
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default HistoricoFinanceiro;
